import {
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import React, { memo } from "react";

import type { PlayerDropData } from "@/features/player-kanban/types/kanbanColumnContentTypes";
import type { Player } from "@/features/player-kanban/types/types";

import RankSortableCard from "@/features/player-kanban/components/organisms/RankSortableCard";
import KanbanPlayerCard from "@/components/organisms/custom/ui/KanbanPlayerCard";
import { Box } from "@/components/atoms/Box";
import { Text } from "@/components/atoms/Text";
import { Stack } from "@/components/atoms/Stack";
import { TEST_ID_PLAYER_KANBAN } from "e2e/testId";

/**
 * Props for KanbanColumnContentView component
 */
interface KanbanColumnContentViewProps {
  /**
   * ID of the column
   */
  columnId: string;
  /**
   * The players to RENDER — already truncated to the lazily-mounted window by
   * the container (SE-5492). This is not necessarily the whole column.
   */
  players: (Player & { initialTags: string[] })[];
  /**
   * SE-5492: total cards in the column, including ones not yet mounted. Used
   * for the last-row margin so it does not depend on the mounted slice.
   */
  totalPlayers?: number;
  /**
   * SE-5492: whether cards remain unmounted below the fold. Drives the
   * sentinel that triggers the next chunk.
   */
  hasMoreCards?: boolean;
  /**
   * SE-5492: ref setter for the lazy-mount sentinel element.
   */
  sentinelRef?: (node: unknown) => void;
  /**
   * Whether the column is empty
   */
  isEmpty: boolean;
  /**
   * Text to display when empty
   */
  emptyText: string;
  /**
   * Whether any item is being dragged globally
   */
  isGloballyDragging: boolean;
  /**
   * Whether dragging from this column
   */
  isDraggingFromThisColumn: boolean;
  /**
   * Whether a drag is over this column
   */
  isOver: boolean;
  /**
   * ID of the board
   */
  boardId?: string;
  /**
   * Trigger for board refresh
   */
  boardRefreshTrigger?: number;
  /**
   * SE-6554 item N: slot the live drop gap is held open at, or -1 for no gap.
   */
  insertionIndex: number;
  /**
   * SE-6554 item N: whether the lane is currently HOLDING the gap open. Distinct
   * from `insertionIndex >= 0`, which only says the lane has been entered at
   * some point during this drag and therefore still owns a slot to animate.
   */
  isDropGapOpen?: boolean;
  /**
   * Computed style classes for the drop zone
   */
  dropZoneClassName: string;
  /**
   * Indicates whether the current user has permission to edit the board
   */
  canEdit?: boolean;
  /**
   * SE-4685: When true (column sorted by Rank + editable), each card renders a
   * grab handle and becomes a within-column drag-to-reorder sortable item.
   */
  isRankReorderEnabled?: boolean;
  /**
   * Ref setter for droppable
   */
  setNodeRef: (node: HTMLElement | null) => void;
  /**
   * Handler factory for player clicks
   */
  createPlayerClickHandler: (playerId: string) => () => void;
  /**
   * Handler for drop events
   */
  handleDrop: (data: PlayerDropData) => void;
  /**
   * Handler for tag additions
   */
  handleTagAdded: (playerId: string, tag: string) => void;
  /**
   * Handler for local drag start
   */
  onLocalDragStart: () => void;
  /**
   * Handler for local drag end
   */
  onLocalDragEnd: () => void;
  /**
   * Handler for auto scroll
   */
  onAutoScroll?: (dragX: number) => void;
  /**
   * Handler for board refresh
   */
  onBoardRefresh?: () => void;
}

/**
 * Chrome shared by both drop-gap states, so the transition has exactly one
 * property — the height — to animate.
 */
const DROP_GAP_BASE =
  "web:transition-all web:duration-200 w-full shrink-0 overflow-hidden rounded-md";

/**
 * Open: the solid info-tinted slot the design draws (Figma 80:16392, the
 * placeholder inside "Column · Drag-over"). That node is a 246×64 fill of
 * `Info/info50` with no stroke — `bg-info-50` is that exact token, and unlike a
 * raw hex it carries a dark value (#05405D) instead of a light blue burning a
 * hole in a dark lane. The dashed accent border this used to have belonged to
 * the older, indicator-style gap; the lane's own dashed outline now carries the
 * "this is the target" message, and a second dashed rectangle inside it read as
 * noise.
 *
 * Its radius matches the card's (`rounded-md`, 8px) because the slot is the
 * shadow of the card that will land in it — a tighter radius reads as a
 * different kind of object sitting in the column.
 *
 * `h-16` is a deliberate, closed-token slot size rather than the dragged card's
 * measured height. A card here is content-sized (avatar, meta, signals, tags and
 * footer all vary), so its height is only knowable at runtime, and a runtime
 * height can reach an element only through the design system's escape-hatch
 * style prop — which is banned in app code and budget-ratcheted besides. A fixed
 * rung says the honest thing anyway: "the card goes HERE", not "the card is
 * exactly this tall".
 */
const DROP_GAP_OPEN = "mb-2 h-16 bg-surface-info-subtle";

/** Closed: zero height, no chrome — present only so it can animate back open. */
const DROP_GAP_CLOSED = "mb-0 h-0";

/**
 * SE-6554 item N — the space a lane opens for the card being dragged into it.
 *
 * Its height transitions between zero and one slot. The cards beneath it are in
 * normal flow, so the browser reflows them continuously across that transition:
 * they SLIDE down as the gap opens and back up as it closes. That sliding is the
 * motion the board was missing — before this, crossing into a lane changed
 * nothing inside the lane at all.
 *
 * It renders in BOTH states rather than mounting on entry, because an element
 * that unmounts cannot animate shut; the lane keeps its slot for the same
 * reason. Presence therefore says nothing about whether a gap is open — the
 * rendered height is the signal.
 *
 * The gap is a placeholder, never the card: nothing moves between lanes until
 * the drop. See the mechanism note in KanbanColumnContentContainer.
 * @param isOpen - Whether the lane is currently holding the gap open
 * @returns The gap element
 */
const buildDropGap = (isOpen: boolean) => (
  <Box
    key="kanban-drop-gap"
    testID={TEST_ID_PLAYER_KANBAN.COLUMN_DROP_GAP}
    className={`${DROP_GAP_BASE} ${isOpen ? DROP_GAP_OPEN : DROP_GAP_CLOSED}`}
  />
);

/**
 * Shared per-row rendering context. Passed to module-scope row helpers so the
 * View stays a pure `() => (...)` component (component-structure/no-return-in-view).
 */
interface ColumnRowContext {
  columnId: string;
  boardId?: string;
  canEdit: boolean;
  insertionIndex: number;
  isDropGapOpen: boolean;
  playersLength: number;
  isRankReorderEnabled: boolean;
  createPlayerClickHandler: (playerId: string) => () => void;
  handleTagAdded: (playerId: string, tag: string) => void;
  onLocalDragStart: () => void;
  onLocalDragEnd: () => void;
  onAutoScroll?: (dragX: number) => void;
  onBoardRefresh?: () => void;
}

/**
 * Build a single player card. Rank-reorder sortable bindings, when active, are
 * injected via RankReorderCardContext by the RankSortableCard wrapper — not via
 * props — so this stays the same element in both modes.
 * @param player - Player (with initial tags) to render
 * @param ctx - Shared row-rendering context
 * @returns The player card element
 */
const buildPlayerCard = (
  player: Player & { initialTags: string[] },
  ctx: ColumnRowContext
) => (
  <KanbanPlayerCard
    playerId={player.id}
    playerName={player.name}
    rank={player.rank}
    daysInStage={player.daysInStage}
    isStale={player.isStale}
    // SE-5492: avatar / position / reports+verdict. `imageUrl` is null for
    // every player today (SE-5538 schema-only) → initials fallback renders.
    imageUrl={player.imageUrl}
    position={player.position}
    reportsCount={player.reportsCount}
    verdict={player.verdict}
    archetype={player.archetype || ""}
    age={player.age || 0}
    gpr={player.gpr || 0}
    contractValue={player.contractValue}
    contractMonths={player.contractMonths}
    agentName={player.agentName || ""}
    teamName={player.teamName || ""}
    playerAgent={player.playerAgent || ""}
    // SE-6490: drives the card footer's "Updated 2d ago".
    updatedAt={player.updatedAt}
    // SE-6866: completes that footer with "· Hugo". The raw id travels here;
    // the card resolves it against the org roster.
    lastEditedByUserId={player.lastEditedByUserId}
    boardId={ctx.boardId}
    bioData={player.bioData}
    initialTags={player.initialTags || []} // Use initialTags from container
    isOptimistic={player.isOptimistic} // Animation flag for new cards
    canEdit={ctx.canEdit}
    onPress={ctx.createPlayerClickHandler(player.id)}
    onTagAdded={ctx.handleTagAdded}
    onDragStart={ctx.onLocalDragStart}
    onDragEnd={ctx.onLocalDragEnd}
    onAutoScroll={ctx.onAutoScroll}
    onBoardRefresh={ctx.onBoardRefresh}
  />
);

/**
 * Render the column's player rows (with insertion indicators). In rank-reorder
 * mode each card is wrapped in RankSortableCard and the list is a SortableContext.
 * @param players - Players (with initial tags) to render
 * @param ctx - Shared row-rendering context
 * @returns The rows element(s)
 */
const renderColumnRows = (
  players: (Player & { initialTags: string[] })[],
  ctx: ColumnRowContext
) => {
  const rows: React.ReactNode[] = players.map((player, index) => (
    // Stable key (no positional index) so cards do not remount when the rank
    // reorder shifts their position (SE-4685 / CodeRabbit).
    <React.Fragment key={`${ctx.columnId}-${player.id}`}>
      <Box
        // SE-5491: stage+player identity on the existing row wrapper (no new
        // DOM node) so the dual-view consistency check can read per-stage
        // card-ID sets straight off the Kanban renderer.
        testID={TEST_ID_PLAYER_KANBAN.columnCardFor(ctx.columnId, player.id)}
        className={`relative w-full shrink-0 ${
          index === ctx.playersLength - 1 ? "mb-0" : "mb-micro"
        }`}
      >
        {ctx.isRankReorderEnabled ? (
          <RankSortableCard
            columnId={ctx.columnId}
            playerId={player.id}
            /* SE-6554: the same fields the card's own draggable publishes, so
               the overlay under the cursor renders a real card in rank-sorted
               columns too. Kept in step with `draggableData` in
               KanbanPlayerCardContainer. */
            dragData={{
              id: player.id,
              playerName: player.name,
              contractValue: player.contractValue,
              contractMonths: player.contractMonths,
              teamName: player.teamName || "",
              age: player.age || 0,
              archetype: player.archetype || "",
              gpr: player.gpr || 0,
            }}
          >
            {buildPlayerCard(player, ctx)}
          </RankSortableCard>
        ) : (
          buildPlayerCard(player, ctx)
        )}
      </Box>
    </React.Fragment>
  ));

  // SE-6554 item N: the drop gap sits at the slot the dragged card would land
  // in. ONE keyed element placed by slicing (not mutating) the row list, so
  // React MOVES it between slots rather than remounting it — crossing a card
  // boundary repositions the gap without restarting its animation from zero.
  const rowsWithGap =
    ctx.insertionIndex >= 0
      ? [
          ...rows.slice(0, Math.min(ctx.insertionIndex, rows.length)),
          buildDropGap(ctx.isDropGapOpen),
          ...rows.slice(Math.min(ctx.insertionIndex, rows.length)),
        ]
      : rows;

  if (!ctx.isRankReorderEnabled) {
    return <>{rowsWithGap}</>;
  }

  return (
    <SortableContext
      items={players.map(player => `kanban-${player.id}`)}
      strategy={verticalListSortingStrategy}
    >
      {rowsWithGap}
    </SortableContext>
  );
};

/**
 * KanbanColumnContentView - Pure presentation component for kanban column content
 * @param root0 - Component props
 * @param root0.columnId - ID of the column
 * @param root0.players - The lazily-mounted slice of players to render (SE-5492)
 * @param root0.totalPlayers - SE-5492: total cards in the column, including unmounted ones
 * @param root0.hasMoreCards - SE-5492: whether cards remain unmounted below the fold
 * @param root0.sentinelRef - SE-5492: ref setter for the lazy-mount sentinel
 * @param root0.isEmpty - Whether the column is empty
 * @param root0.emptyText - Text to display when empty
 * @param root0.isGloballyDragging - Whether any item is being dragged globally
 * @param root0.isDraggingFromThisColumn - Whether dragging from this column
 * @param root0.isOver - Whether a drag is over this column
 * @param root0.boardId - ID of the board
 * @param root0.boardRefreshTrigger - Trigger for board refresh
 * @param root0.insertionIndex - Slot the live drop gap is held at, or -1 (SE-6554)
 * @param root0.isDropGapOpen - Whether the lane is holding the drop gap open (SE-6554)
 * @param root0.dropZoneClassName - Computed style classes for the drop zone
 * @param root0.setNodeRef - Ref setter for droppable
 * @param root0.createPlayerClickHandler - Handler factory for player clicks
 * @param root0.handleDrop - Handler for drop events
 * @param root0.handleTagAdded - Handler for tag additions
 * @param root0.onLocalDragStart - Handler for local drag start
 * @param root0.onLocalDragEnd - Handler for local drag end
 * @param root0.onAutoScroll - Handler for auto scroll
 * @param root0.onBoardRefresh - Handler for board refresh
 * @param root0.canEdit - Indicates whether the current user has permission to edit the board
 * @param root0.isRankReorderEnabled - SE-4685: whether Rank-sort within-column drag-reorder is active
 */
const KanbanColumnContentView = ({
  columnId,
  players,
  totalPlayers,
  hasMoreCards = false,
  sentinelRef,
  isEmpty,
  emptyText,
  isGloballyDragging: _isGloballyDragging,
  isDraggingFromThisColumn: _isDraggingFromThisColumn,
  isOver,
  boardId,
  boardRefreshTrigger: _boardRefreshTrigger,
  insertionIndex,
  isDropGapOpen = false,
  dropZoneClassName,
  canEdit = true,
  isRankReorderEnabled = false,
  setNodeRef,
  createPlayerClickHandler,
  handleDrop: _handleDrop,
  handleTagAdded,
  onLocalDragStart,
  onLocalDragEnd,
  onAutoScroll,
  onBoardRefresh,
}: KanbanColumnContentViewProps) => (
  <Box ref={setNodeRef} className={dropZoneClassName}>
    {isEmpty ? (
      <Box className="flex-1">
        {/*
         * Empty stage per Figma `Column-Pipeline` empty state (80:15030): a
         * dashed drop zone inset to the column's content width, 8px radius,
         * `border300` dashes, and the label at 12px muted — dev had a 4px
         * radius, the lighter `border100` dashes, and a 14px label.
         *
         * The accent state keys off `isOver`, not off "a drag is happening
         * somewhere": exactly one lane wears chrome during a drag — the aimed
         * one. Lighting every empty lane blue on every drag is the same
         * all-lanes-at-once noise the filled lanes' dashed hint was removed for.
         */}
        <Box
          {...{
            "data-testid": TEST_ID_PLAYER_KANBAN.EMPTY_COLUMN_PLACEHOLDER,
          }}
          className={`relative z-0 min-h-24 flex-1 items-center justify-center rounded-md border border-dashed p-4 ${
            isOver
              ? "border-accent-primary bg-accent-primary/10"
              : "border-outline-strong bg-surface-raised"
          }`}
        >
          <Stack className="items-center gap-2">
            <Text variant="caption" className="text-center text-content-muted">
              {emptyText}
            </Text>
            {isOver && (
              <Text
                variant="unstyled"
                className="text-center font-medium text-caption text-accent-primary"
              >
                🎯 Drop players here
              </Text>
            )}
          </Stack>

          {/* SE-6554 item N: an empty lane needs no drop gap — the whole dashed
              placeholder IS the gap, and it already lights up on `isOver`
              through `dropZoneClassName`. The 4px bar that used to render here
              was driven by the old fixed insertion index. */}
        </Box>
      </Box>
    ) : (
      /* No scrolling - columns should show all content.
       *
       * Column interior per Figma 80:15027. The drop zone's `m-3` above already
       * IS the design's 12px content inset (cards frame at x=12 in a 264px
       * column, 12px off the bottom); the `px-2 pt-2` that used to sit here
       * doubled it to 20px and pushed the first card 28px below the stage name
       * instead of the design's 20px. With it gone the header's `pb-2` plus that
       * 12px margin compose the 20px exactly.
       *
       * Cards are 8px apart. That gap has to come from the Stack's `space`
       * prop: a hand-written `gap-*` className on a Stack renders as 0px (see
       * the same note in KanbanColumnView), so the previous `gap-micro` was
       * spacing nothing and the cards read as one block.
       */
      <Box className="flex flex-1 flex-col overflow-visible">
        <Stack space="2" className="flex flex-col items-stretch">
          {renderColumnRows(players, {
            columnId,
            boardId,
            canEdit,
            insertionIndex,
            isDropGapOpen,
            playersLength: totalPlayers ?? players.length,
            isRankReorderEnabled,
            createPlayerClickHandler,
            handleTagAdded,
            onLocalDragStart,
            onLocalDragEnd,
            onAutoScroll,
            onBoardRefresh,
          })}

          {/* SE-5492: lazy-mount sentinel. Rendered ONLY while cards remain
              unmounted, so its presence/absence is itself the assertion that
              a column is (or is no longer) lazily mounting. It is a zero-height
              marker — it must not add visible space to the column. */}
          {hasMoreCards ? (
            <Box
              ref={sentinelRef}
              testID={TEST_ID_PLAYER_KANBAN.COLUMN_LAZY_SENTINEL}
              className="h-px w-full shrink-0"
            />
          ) : null}
        </Stack>
      </Box>
    )}
  </Box>
);

KanbanColumnContentView.displayName = "KanbanColumnContentView";

export default memo(KanbanColumnContentView);
